import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Layout, Spin, Typography } from 'antd'
import { publicFetchClient } from '../../../api/main-api-client'
import { HeaderAuth } from '../components/HeaderAuth'
import { CodeStep } from '../components/PhoneAuth/CodeStep'
import { InvitationInvalid } from '../components/PhoneAuth/InvitationInvalid'
import { PhoneNumberStep } from '../components/PhoneAuth/PhoneNumberStep'
import { usePhoneCode } from '../hooks/usePhoneCode'
import { contentStyle, layoutStyle, textSubtitle } from '../styles/AuthStyles'
import type { PhoneVerifyResult } from '../types/phoneAuth'
import { unwrap } from '../utils/apiCall'
import { applyVerifyResult } from '../utils/applyVerifyResult'
import { getPhoneAuthErrorCode, getPhoneAuthErrorMessage, getPhoneAuthErrorStatus } from '../utils/phoneAuthErrors'
import { maskPhone } from '../utils/phoneNumber'

const isInvitationLinkError = (error: unknown) => {
  const status = getPhoneAuthErrorStatus(error)
  const code = getPhoneAuthErrorCode(error)

  // Invitation-level errors (not found, used, expired, attempts limit) are 404 or 400 without a `code`.
  // 410 is an expired SMS code and 429 the rate limiter: both stay in the flow.
  return status === 404 || (status === 400 && !code)
}

const PhoneInvitation = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [anotherNumber, setAnotherNumber] = useState(false)
  const [linkInvalid, setLinkInvalid] = useState(false)

  const {
    data: invitation,
    isPending,
    error: startError,
  } = useQuery({
    queryKey: ['invitation-start', token],
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: () => unwrap(publicFetchClient.POST('/invitations/start', { body: { token: token ?? '' } })),
  })

  const flow = usePhoneCode<PhoneVerifyResult>({
    sendCode: (phone) =>
      unwrap(publicFetchClient.POST('/invitations/send-code', { body: { token: token ?? '', phone_number: phone } })),
    verifyCode: (phone, code) =>
      unwrap(
        publicFetchClient.POST('/invitations/verify-code', {
          body: { token: token ?? '', phone_number: phone, code },
        }),
      ),
    onError: (error) => {
      if (isInvitationLinkError(error)) setLinkInvalid(true)
    },
    onVerified: (result, phone) => {
      const outcome = applyVerifyResult(result, { phone, email: invitation?.email, emailVerified: true })

      void navigate(outcome === 'signed_in' ? '/patient-portal/dashboard' : '/patient-portal/register?step=found')
    },
  })

  const hint = invitation?.phone_hint
  const target = flow.phone ? maskPhone(flow.phone) : `...${(hint ?? '').replace(/\D/g, '').slice(-4)}`

  const renderBody = () => {
    if (!token || linkInvalid || (startError && isInvitationLinkError(startError))) {
      return <InvitationInvalid />
    }

    if (startError) {
      return (
        <Alert
          type='error'
          showIcon
          title={getPhoneAuthErrorMessage(startError, t)}
          data-testid='invitation-error'
        />
      )
    }

    if (isPending) return <Spin data-testid='invitation-loading' />

    if (flow.stage === 'code') {
      return (
        <CodeStep
          title={t('phone_auth_verify_title')}
          subtitle={t('phone_auth_code_sent_to', { target })}
          onSubmit={flow.verify}
          loading={flow.verifying}
          error={flow.error}
          cooldown={flow.cooldown}
          onResend={flow.resend}
        />
      )
    }

    if (!hint || anotherNumber) {
      return (
        <PhoneNumberStep
          title={t('phone_auth_invitation_title')}
          subtitle={hint ? t('phone_auth_invitation_other_number') : t('phone_auth_invitation_no_number')}
          onSubmit={flow.send}
          loading={flow.sending}
          error={flow.error}
        >
          {hint && (
            <Button
              type='link'
              block
              className='mt-2'
              onClick={() => setAnotherNumber(false)}
              data-testid='invitation-use-record-number'
            >
              {t('phone_auth_invitation_use_record_number')}
            </Button>
          )}
        </PhoneNumberStep>
      )
    }

    return (
      <div data-testid='invitation-confirm'>
        <Typography.Title
          level={2}
          style={{ textAlign: 'center', marginBottom: '16px', fontWeight: 400 }}
        >
          {t('phone_auth_invitation_title')}
        </Typography.Title>
        <Typography.Text
          type='secondary'
          style={{ ...textSubtitle, marginBottom: '40px' }}
          data-testid='invitation-hint'
        >
          {t('phone_auth_invitation_hint', { hint })}
        </Typography.Text>
        {flow.error && (
          <Alert
            type='error'
            showIcon
            style={{ marginBottom: '16px' }}
            title={flow.error}
            data-testid='invitation-send-error'
          />
        )}
        <Button
          type='primary'
          size='large'
          block
          loading={flow.sending}
          onClick={() => flow.send(null)}
          data-testid='invitation-send-code'
        >
          {t('send_code_cta')}
        </Button>
        <Button
          type='link'
          block
          className='mt-2'
          onClick={() => setAnotherNumber(true)}
          data-testid='invitation-another-number'
        >
          {t('phone_auth_invitation_another_number')}
        </Button>
      </div>
    )
  }

  return (
    <Layout style={layoutStyle}>
      <HeaderAuth />
      <Layout.Content style={contentStyle}>
        <div
          className='mb-20 w-full max-w-100'
          data-testid='phone-invitation'
        >
          {renderBody()}
        </div>
      </Layout.Content>
    </Layout>
  )
}

export default PhoneInvitation
